// React
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Refine
import { useTranslation } from '@refinedev/core';

// Mantine
import {
  Group,
  Loader,
  MultiSelect,
  Text,
  useComputedColorScheme,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';

// API
import {
  useGetIntakeItemsByIDs,
  usePaginateIntakeItemsBySite,
} from 'api/resource';

// Store
import { useCurrentSiteStore } from 'store/SiteStore';

// Utils
import {
  appendUniqueItemsByID,
  dedupeItemsByID,
  hasSameItemIDs,
  matchesUploadSearch,
  passthroughOptionsFilter,
  UPLOAD_LINK_PAGE_SIZE,
  UploadSelectOption,
  upsertUploadOption,
} from 'utils/uploadLinkSelectUtils';
import { IntakeItem } from '@billing/validation';

interface LinkedUploadFilterSelectProps {
  value?: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
}

const EMPTY_INTAKE_ITEMS: IntakeItem[] = [];
const LOADING_MORE_OPTION_VALUE = '__upload_filter_loading_more__';

export const LinkedUploadFilterSelect: React.FC<
  LinkedUploadFilterSelectProps
> = ({ value = [], onChange, disabled = false }) => {
  const { translate } = useTranslation();
  const computedColorScheme = useComputedColorScheme('dark');
  const { id: siteID } = useCurrentSiteStore();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue.trim(), 300);
  const [page, setPage] = useState(0);
  const [pagedItems, setPagedItems] = useState<IntakeItem[]>([]);
  const seenItemsByIDRef = useRef<Map<string, IntakeItem>>(new Map());

  const selectedUploadIDs = Array.isArray(value) ? value : [];
  const selectedUploadsQuery = useGetIntakeItemsByIDs({
    ids: selectedUploadIDs,
  });

  useEffect(() => {
    seenItemsByIDRef.current.clear();
    setPage(0);
    setPagedItems([]);
  }, [siteID]);

  useEffect(() => {
    setPage(0);
    setPagedItems([]);
  }, [debouncedSearch]);

  const intakeItemsQuery = usePaginateIntakeItemsBySite({
    siteID,
    page,
    pageSize: UPLOAD_LINK_PAGE_SIZE,
    globalFilter: debouncedSearch,
  });

  const intakeItems = intakeItemsQuery.data?.data ?? EMPTY_INTAKE_ITEMS;
  const loadedItems = pagedItems.length > 0 ? pagedItems : intakeItems;
  const totalAvailable = intakeItemsQuery.data?.total || 0;
  const hasMore = loadedItems.length < totalAvailable;
  const isLoadingNextPage = intakeItemsQuery.isFetching && page > 0;
  const selectedUploads = selectedUploadsQuery.data;

  useEffect(() => {
    if (intakeItemsQuery.isPlaceholderData) return;

    if (page === 0) {
      const next = dedupeItemsByID(intakeItems);
      setPagedItems((current) =>
        hasSameItemIDs(current, next) ? current : next
      );
      return;
    }

    setPagedItems((current) => appendUniqueItemsByID(current, intakeItems));
  }, [intakeItemsQuery.isPlaceholderData, intakeItems, page]);

  useEffect(() => {
    pagedItems.forEach((item) => {
      seenItemsByIDRef.current.set(item.id, item);
    });

    selectedUploads.forEach((item) => {
      if (item?.id) {
        seenItemsByIDRef.current.set(item.id, item);
      }
    });
  }, [pagedItems, selectedUploads]);

  const { selectData, optionProviderByID } = useMemo(() => {
    const optionsByID = new Map<string, UploadSelectOption>();
    const providerByID = new Map<string, string>();

    loadedItems.forEach((item) => {
      upsertUploadOption({
        item,
        translate,
        optionsByID,
        providerByID,
      });
    });

    selectedUploads.forEach((selectedUpload) => {
      if (!selectedUpload?.id || optionsByID.has(selectedUpload.id)) return;
      upsertUploadOption({
        item: selectedUpload,
        translate,
        optionsByID,
        providerByID,
      });
    });

    // Keep selected values visible even when they are not in the currently
    // loaded page of intake items.
    selectedUploadIDs.forEach((uploadID) => {
      if (!uploadID || optionsByID.has(uploadID)) return;
      optionsByID.set(uploadID, {
        value: uploadID,
        label: uploadID,
      });
    });

    if (debouncedSearch.trim()) {
      seenItemsByIDRef.current.forEach((item) => {
        if (
          matchesUploadSearch(item, debouncedSearch, translate) &&
          !optionsByID.has(item.id)
        ) {
          upsertUploadOption({
            item,
            translate,
            optionsByID,
            providerByID,
          });
        }
      });
    }

    if (isLoadingNextPage && hasMore) {
      optionsByID.set(LOADING_MORE_OPTION_VALUE, {
        value: LOADING_MORE_OPTION_VALUE,
        label: translate(
          'pages:claims.table.columns.linkedUpload.loadingMore',
          'Loading more uploads...'
        ),
        disabled: true,
      });
    }

    return {
      selectData: Array.from(optionsByID.values()),
      optionProviderByID: providerByID,
    };
  }, [
    loadedItems,
    selectedUploadIDs,
    selectedUploads,
    debouncedSearch,
    hasMore,
    isLoadingNextPage,
    translate,
  ]);

  const loading =
    intakeItemsQuery.isLoading ||
    (intakeItemsQuery.isFetching && page === 0) ||
    selectedUploadsQuery.isLoading;
  const disabledBecauseNoSite = !siteID;
  const effectiveDisabled = disabled || disabledBecauseNoSite;
  const placeholder = disabledBecauseNoSite
    ? translate(
        'pages:claims.table.columns.linkedUpload.selectSiteFirst',
        'Select a site first'
      )
    : translate(
        'pages:claims.table.columns.linkedUpload.filterPlaceholder',
        'Filter by Upload'
      );

  return (
    <MultiSelect
      variant="unstyled"
      className="mrt-filter-text-input"
      searchable
      clearable
      hidePickedOptions
      value={selectedUploadIDs}
      data={selectData}
      searchValue={searchValue}
      onSearchChange={setSearchValue}
      onChange={(nextValues) => {
        const cleanedValues = nextValues.filter(
          (value) => value !== LOADING_MORE_OPTION_VALUE
        );
        if (
          nextValues.includes(LOADING_MORE_OPTION_VALUE) &&
          cleanedValues.length === 0
        ) {
          return;
        }
        onChange(cleanedValues);
      }}
      placeholder={placeholder}
      aria-label={placeholder}
      title={placeholder}
      renderOption={({ option }) => {
        if (option.value === LOADING_MORE_OPTION_VALUE) {
          return (
            <Group gap={8} wrap="nowrap">
              <Loader size={14} />
              <Text size="sm">{option.label}</Text>
            </Group>
          );
        }

        const providerName = optionProviderByID.get(option.value);
        return (
          <Text size="sm">
            {providerName ? `${option.label} | ${providerName}` : option.label}
          </Text>
        );
      }}
      filter={passthroughOptionsFilter}
      scrollAreaProps={{
        onBottomReached: () => {
          if (effectiveDisabled || !hasMore || intakeItemsQuery.isFetching)
            return;
          setPage((current) => current + 1);
        },
      }}
      nothingFoundMessage={
        loading
          ? translate(
              'pages:claims.table.columns.linkedUpload.searching',
              'Searching uploads...'
            )
          : translate(
              'pages:claims.table.columns.linkedUpload.noUploadsFound',
              'No uploads found'
            )
      }
      rightSection={loading ? <Loader size={16} /> : null}
      disabled={effectiveDisabled}
      clearButtonProps={{ size: 'md' }}
      styles={{
        root: {
          borderBottom: `2px solid ${
            computedColorScheme === 'dark'
              ? 'var(--mantine-color-gray-7)'
              : 'var(--mantine-color-gray-3)'
          }`,
          fontWeight: 'normal',
          minWidth: 'calc(6.25rem * var(--mantine-scale))',
          width: '100%',
        },
      }}
      maxDropdownHeight={320}
      comboboxProps={{ withinPortal: true }}
      data-testid="linked-upload-filter-select"
    />
  );
};
